/**
 * @file LanguageChart.jsx
 * @description LanguageChart component for displaying and managing language.
 * @component LanguageChart
 * @exports LanguageChart - Main component for displaying and managing language.
 */

import React, { useMemo } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { Line } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

// ── Helpers ────────────────────────────────────────────────────────
// Parse YYYY-MM-DD as local date, not UTC.
// new Date("2026-04-24") is UTC midnight → lands on Apr 23 in negative-offset
// timezones, which is why the chart was missing the last day.
const parseLocalDate = (val) => {
    if (!val) return null;
    const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
};

// Sortable YYYY-MM-DD key in local time. YMD strings sort lexicographically
// the same as chronologically, which the old MM/DD/YYYY format didn't.
const toLocalYMD = (val) => {
    const d = parseLocalDate(val);
    if (!d || Number.isNaN(d.getTime())) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// "Apr 24" — accepts a YYYY-MM-DD key from toLocalYMD.
const prettyLabel = (ymd) => {
    const d = parseLocalDate(ymd);
    return d
        ? d.toLocaleDateString(undefined, { month: "short", day: "2-digit" })
        : ymd;
};

const LanguageDailyStackedAreaChart = ({ rows, title = "Language" }) => {
    const { labels, englishCounts, spanishCounts } = useMemo(() => {
        const map = new Map();
        (rows || []).forEach((r) => {
            const key = toLocalYMD(r?.ConversationDate);
            if (!key) return;
            const cur = map.get(key) || { english: 0, spanish: 0 };
            cur.english += Number(r?.English) || 0;
            cur.spanish += Number(r?.Spanish) || 0;
            map.set(key, cur);
        });
        const keys = [...map.keys()].sort();
        return {
            labels:        keys.map(prettyLabel),
            englishCounts: keys.map(k => map.get(k)?.english ?? 0),
            spanishCounts: keys.map(k => map.get(k)?.spanish ?? 0),
        };
    }, [rows]);

    if (!rows?.length) return null;

    const data = {
        labels,
        datasets: [
            {
                label: "English",
                data: englishCounts,
                backgroundColor: "#DCEAF7",
                fill: true, stack: "lang",
                borderWidth: 0, pointRadius: 0, pointHoverRadius: 0, tension: 0.35,
            },
            {
                label: "Spanish",
                data: spanishCounts,
                backgroundColor: "#A6CAEC",
                fill: true, stack: "lang",
                borderWidth: 0, pointRadius: 0, pointHoverRadius: 0, tension: 0.35,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
            legend: {
                position: "top",
                labels: { boxWidth: 10, boxHeight: 10, font: { size: 11 } },
            },
            tooltip: { enabled: true },
        },
        elements: { line: { borderWidth: 0 }, point: { radius: 0 } },
        scales: {
            x: { grid: { display: false }, ticks: { font: { size: 10 } } },
            y: {
                stacked: true,
                beginAtZero: true,
                ticks: { precision: 0, font: { size: 10 } },
                grid: { color: "rgba(0,0,0,0.04)" },
            },
        },
    };

    return (
        <Paper elevation={0} sx={{
            border: "0.5px solid", borderColor: "divider",
            borderRadius: 2, p: "12px 14px", mb: 2,
        }}>
            <Typography sx={{
                fontSize: 11, fontWeight: 500, color: "text.secondary",
                textTransform: "uppercase", letterSpacing: "0.05em", mb: 1,
            }}>
                {title}
            </Typography>
            <Box sx={{ height: 260 }}>
                <Line data={data} options={options} />
            </Box>
        </Paper>
    );
};

export default LanguageDailyStackedAreaChart;